/**
 * Minimum Order Value Validation
 * Prevents checkout if order total is less than $50 (excluding GST)
 */

(function() {
  'use strict';

  // Minimum order value in cents ($50.00 = 5000 cents)
  const MIN_ORDER_VALUE_CENTS = 5000;
  const GST_RATE = 0.1; // 10% GST

  /**
   * Calculate subtotal excluding GST
   * @param {number} totalPriceCents - Total price in cents
   * @param {boolean} taxesIncluded - Whether taxes are included in the price
   * @returns {number} Subtotal excluding GST in cents
   */
  function calculateSubtotalExcludingGST(totalPriceCents, taxesIncluded) {
    if (taxesIncluded) {
      // If taxes are included, divide by (1 + GST rate) to get subtotal excluding GST
      return Math.round(totalPriceCents / (1 + GST_RATE));
    } else {
      // If taxes are not included, the total price is already excluding GST
      return totalPriceCents;
    }
  }

  /**
   * Validate minimum order value
   * @param {number} totalPriceCents - Total price in cents
   * @param {boolean} taxesIncluded - Whether taxes are included
   * @returns {Object} Validation result with isValid and message
   */
  function validateMinimumOrder(totalPriceCents, taxesIncluded) {
    const subtotalExclGST = calculateSubtotalExcludingGST(totalPriceCents, taxesIncluded);
    const isValid = subtotalExclGST >= MIN_ORDER_VALUE_CENTS;
    
    return {
      isValid: isValid,
      subtotalExclGST: subtotalExclGST,
      requiredAmount: MIN_ORDER_VALUE_CENTS,
      shortfall: isValid ? 0 : (MIN_ORDER_VALUE_CENTS - subtotalExclGST)
    };
  }

  /**
   * Format money amount
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Theme?.settings?.moneyFormat || '{{amount}}');
    }
    // Fallback formatting
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Show error message
   * @param {string} message - Error message to display
   */
  function showErrorMessage(message) {
    // Remove existing error messages
    const existingErrors = document.querySelectorAll('[data-min-order-error]');
    existingErrors.forEach(err => err.remove());

    // Create error message element
    const errorDiv = document.createElement('div');
    errorDiv.setAttribute('data-min-order-error', '');
    errorDiv.className = 'min-order-error';
    errorDiv.innerHTML = `
      <div class="min-order-error-content">
        <span class="min-order-error-icon">⚠️</span>
        <span class="min-order-error-text">${message}</span>
      </div>
    `;

    // Try to insert in cart page first
    const checkoutButton = document.querySelector('.cart-checkout [name="checkout"]');
    if (checkoutButton) {
      checkoutButton.closest('.cart-checkout')?.insertBefore(errorDiv, checkoutButton);
      return;
    }
    
    // Try add-to-cart banner
    const bannerCheckoutButton = document.querySelector('.atc-button--checkout');
    if (bannerCheckoutButton) {
      const bannerFooter = bannerCheckoutButton.closest('.atc-banner--cart-footer');
      if (bannerFooter) {
        bannerFooter.insertBefore(errorDiv, bannerCheckoutButton.closest('form'));
      }
      return;
    }
    
    // Fallback: insert at the end of cart-total or cart section
    const cartTotal = document.querySelector('.cart-total');
    if (cartTotal) {
      cartTotal.appendChild(errorDiv);
    } else {
      const cartSection = document.querySelector('.cart--section, .cartitems--container');
      if (cartSection) {
        cartSection.insertBefore(errorDiv, cartSection.firstChild);
      }
    }
  }

  /**
   * Remove error message
   */
  function removeErrorMessage() {
    const existingErrors = document.querySelectorAll('[data-min-order-error]');
    existingErrors.forEach(err => err.remove());
  }

  /**
   * Update disclaimer visibility based on cart total
   */
  function updateDisclaimer() {
    const disclaimer = document.querySelector('[data-min-order-disclaimer]');
    if (!disclaimer) return;

    // Try to get actual cart data
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const taxesIncluded = cart.taxes_included || false;
        const validation = validateMinimumOrder(cart.total_price, taxesIncluded);
        
        // Show disclaimer if order is below minimum
        if (!validation.isValid) {
          disclaimer.style.display = 'block';
        } else {
          disclaimer.style.display = 'none';
        }
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });
  }

  /**
   * Handle checkout form submission
   * @param {Event} event - Form submit event
   */
  function handleCheckoutSubmit(event) {
    // Try to get cart data from page first
    const cartDataEl = document.querySelector('[data-cart-data]');
    let cartData = null;
    
    if (cartDataEl) {
      try {
        cartData = JSON.parse(cartDataEl.textContent);
      } catch (e) {
        console.error('Error parsing cart data from page:', e);
      }
    }
    
    // If we have cart data from page, use it; otherwise fetch from API
    const cartPromise = cartData 
      ? Promise.resolve(cartData)
      : fetch('/cart.js').then(response => response.json());
    
    cartPromise
      .then(cart => {
        const taxesIncluded = cart.taxes_included || false;
        const validation = validateMinimumOrder(cart.total_price, taxesIncluded);

        if (!validation.isValid) {
          event.preventDefault();
          event.stopPropagation();
          
          const shortfallFormatted = formatMoney(validation.shortfall);
          const requiredFormatted = formatMoney(validation.requiredAmount);
          
          const errorMessage = `A minimum order value of ${requiredFormatted} (excluding GST) is required to complete your purchase. Please add ${shortfallFormatted} more to your cart.`;
          
          showErrorMessage(errorMessage);
          
          // Scroll to error message (try cart page first, then banner)
          const errorEl = document.querySelector('[data-min-order-error]');
          if (errorEl) {
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          // If error is in banner, also scroll to banner
          const bannerError = document.querySelector('.atc-banner [data-min-order-error]');
          if (bannerError) {
            const banner = document.querySelector('.atc-banner');
            if (banner) {
              banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        } else {
          removeErrorMessage();
        }
      })
      .catch(error => {
        console.error('Error validating cart:', error);
        // Allow submission if validation fails (fail open)
      });
  }

  /**
   * Initialize minimum order validation
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Find all checkout forms
    const checkoutForms = document.querySelectorAll('form[action*="/cart"], form[aria-label*="checkout"]');
    
    checkoutForms.forEach(form => {
      form.addEventListener('submit', handleCheckoutSubmit, true); // Use capture phase
    });

    // Also handle checkout buttons that might submit forms programmatically
    const checkoutButtons = document.querySelectorAll('[name="checkout"], .atc-button--checkout');
    checkoutButtons.forEach(button => {
      button.addEventListener('click', function(e) {
        const form = this.closest('form');
        if (form) {
          // Let the form submit handler take care of validation
          // But we need to check before form submits
          const cartDataEl = document.querySelector('[data-cart-data]');
          let cartData = null;
          
          if (cartDataEl) {
            try {
              cartData = JSON.parse(cartDataEl.textContent);
            } catch (err) {
              // Ignore parse errors
            }
          }
          
          if (cartData) {
            const taxesIncluded = cartData.taxes_included || false;
            const validation = validateMinimumOrder(cartData.total_price, taxesIncluded);
            
            if (!validation.isValid) {
              e.preventDefault();
              e.stopPropagation();
              
              // Trigger form submit to show error
              form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }
          }
          return;
        }
        
        // If button is not in a form, prevent default and handle manually
        e.preventDefault();
        const cartForm = document.querySelector('form[action*="/cart"]');
        if (cartForm) {
          cartForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }, true); // Use capture phase
    });

    // Update disclaimer on page load
    updateDisclaimer();

    // Function to update cart data JSON element
    function updateCartDataElement() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartDataEl = document.querySelector('[data-cart-data]');
          if (cartDataEl) {
            cartDataEl.textContent = JSON.stringify({
              total_price: cart.total_price,
              taxes_included: cart.taxes_included || false
            });
          }
          updateDisclaimer();
          removeErrorMessage();
        })
        .catch(error => {
          console.error('Error updating cart data:', error);
        });
    }

    // Listen for cart updates
    window.addEventListener('cartcount:update', function() {
      setTimeout(updateCartDataElement, 100);
    });

    // Also listen for cart drawer updates if using AJAX cart
    document.addEventListener('cart:updated', function() {
      setTimeout(updateCartDataElement, 100);
    });
    
    // Listen for custom cart update events
    document.addEventListener('cart:change', function() {
      setTimeout(updateCartDataElement, 100);
    });
  }

  // Initialize
  init();
})();
